import { ACTIONS, RESOURCES } from "../constants/permissions.constants";
import { User, Procedure, TrainingPlan } from "../types";

/**
 * Helper function to check if a user has access to a department
 */
export const userHasAccessToDepartment = (
  user: User | null,
  departmentId: string,
  ability?: any
): boolean => {
  if (!user) return false;

  // If ability is provided, check for global read permission
  if (ability && ability.can(ACTIONS.READ, RESOURCES.DEPARTMENT)) {
    return true;
  }

  // Admin and Director can access all departments
  if (user.userRole === 'Admin' || user.userRole === 'Director' || user.jobRole === 'HSEQ Manager' || user.userRole === 'SD_Admin_Role') {
    return true;
  }

  if (typeof user.department === 'string' && user.department == departmentId) return true;

  if (typeof user.department !== 'string') {
    if (user.department.id == departmentId) return true;

    if (user.department.departmentCode == departmentId) return true;

    // if (user.userRole === 'Manager' && user.departments && user.departments.length > 0) {
    //   return user.departments.includes(departmentId);
    // }
  }

  return false;
};

/**
 * Check if a user can view a specific procedure
 */
export const canUserViewProcedure = (
  currentUser: User | null,
  procedure: Procedure | null,
  ability?: any
): boolean => {
  if (!currentUser || !procedure) return false;

  // If ability is provided, check for global read permission
  if (ability && ability.can(ACTIONS.READ, RESOURCES.PROCEDURE)) {
    return true;
  }

  // Admin and Director can view all procedures
  if (currentUser.userRole === 'Admin' || currentUser.userRole === 'Director' || currentUser.jobRole === 'HSEQ Manager' || currentUser.userRole === 'SD_Admin_Role') {
    return true;
  }

  // Check if user is a viewer
  if (procedure.viewers && procedure.viewers.some(v => {
    const viewerId = typeof v === 'string' ? v : v.id;
    return viewerId === currentUser.id;
  })) {
    return true;
  }

  // Check if user has access to the procedure's department
  const deptId = procedure.departmentId || (procedure.department?.id) || "";
  return userHasAccessToDepartment(currentUser, deptId, ability);
};

/**
 * Check if a user is explicitly a viewer of a procedure
 */
export const isUserViewerOfProcedure = (
  user: User | null,
  procedure: Procedure | null
): boolean => {
  if (!user || !procedure) return false;
  return !!(procedure.viewers && procedure.viewers.some(v => {
    const viewerId = typeof v === 'string' ? v : v.id;
    return viewerId === user.id;
  }));
};

/**
 * Get a list of procedures that the current user is allowed to view
 */
export const getProceduresVisibleTocurrentUser = (
  currentUser: User | null,
  allProcedures: Procedure[]
): Procedure[] => {
  if (!currentUser) return [];

  // Admin and Director can view all procedures
  if (currentUser.userRole === 'Admin' || currentUser.userRole === 'Director') {
    return allProcedures;
  }

  // Filter procedures based on departments the user has access to
  return allProcedures.filter(
    procedure => canUserViewProcedure(currentUser, procedure)
  );
};

/**
 * Check if a user can edit a specific procedure
 */
export const canUserEditProcedure = (
  currentUser: User | null,
  procedure: Procedure | null
): boolean => {
  if (!currentUser || !procedure) return false;

  // Admin and Director can edit all procedures
  if (currentUser.userRole === 'Admin' || currentUser.userRole === 'Director') {
    return true;
  }

  // Managers can edit procedures from their departments
  const deptId = procedure.departmentId || (procedure.department?.id) || "";
  if (currentUser.userRole === 'Manager') {
    return userHasAccessToDepartment(currentUser, deptId);
  }

  if (currentUser.jobRole === 'HSEQ Manager') {
    return userHasAccessToDepartment(currentUser, deptId);
  }

  // Workers cannot edit procedures
  return false;
};

/**
 * Check if a user can archive/unarchive a procedure
 * Only Admin, Director, and department Managers can archive procedures
 */
export const canUserArchiveProcedure = (
  currentUser: User | null,
  procedure: Procedure | null
): boolean => {
  // Same permissions as editing
  return canUserEditProcedure(currentUser, procedure);
};

/**
 * Filter procedures based on archived status
 */
export const filterProceduresByArchivedStatus = (
  procedures: Procedure[],
  showArchived: boolean
): Procedure[] => {
  return procedures.filter(procedure =>
    showArchived ? procedure.archived : !procedure.archived
  );
};

/**
 * Check if a procedure can be deleted based on its usage in training plans
 * A procedure can NEVER be deleted if it has ever been part of any training plan, regardless of the plan's status
 */
export const canDeleteProcedure = (
  procedure: Procedure,
  trainingPlans: TrainingPlan[]
): { canDelete: boolean; hasTrainingPlanHistory: boolean } => {
  // Check if procedure has ever been part of any training plan
  const hasTrainingPlanHistory = trainingPlans.some(plan =>
    plan.procedures.includes(procedure)
  );

  return {
    canDelete: !hasTrainingPlanHistory,
    hasTrainingPlanHistory
  };
};

/**
 * Get a list of training plans that use a procedure
 * Used for warning messages when archiving
 */
export const getTrainingPlansUsingProcedure = (
  procedure: Procedure,
  trainingPlans: TrainingPlan[]
): TrainingPlan[] => {
  return trainingPlans.filter(plan =>
    !plan.deleted &&
    plan.procedures.includes(procedure)
  );
};

export const canArchiveProcedure = (procedure: Procedure, trainingPlans: TrainingPlan[]) => {
  const activeTrainingPlans = trainingPlans.filter(plan =>
    !plan.deleted &&
    !plan.archived &&
    (plan.status === "Not Started" || plan.status === "In Progress") &&
    plan.procedures.includes(procedure)
  );

  return {
    canArchive: activeTrainingPlans.length === 0,
    activePlans: activeTrainingPlans
  };
}; 